import debounce from 'lodash/debounce'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useMatch, useSearchParams } from 'react-router'
import { Button } from '@/components/ui/button'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { DeployTemplateSheet } from '@/routes/templates/deploy-template-sheet'
import { TemplateCard } from '@/routes/templates/template-card'
import type {
  TemplatesQuery,
  TemplatesQueryVariables,
} from '@/routes/templates/template-cards/index.types.gen'

const PAGE_SIZE = 12

const TEMPLATES_QUERY = gql`
  query Templates(
    $idCompany: String!
    $offset: Int
    $limit: Int
    $filters: JSON
    $orderBy: JSON
  ) {
    templates(
      offset: $offset
      limit: $limit
      filters: $filters
      orderBy: $orderBy
    ) {
      id
      total
      items {
        id
        name
        displayName
        description
        categorySlug
        logoUrl
        services {
          id
          serviceType
        }
        instances(idCompany: $idCompany) @live {
          id
          idTemplate
          status
        }
      }
    }
  }
`

type Props = {
  filterTerm: string
  category: string
}

export const TemplateCards = ({ filterTerm, category }: Props) => {
  const { idCompany } = useIdCompany()
  const { navigateToPath } = useCompanyPath()
  const [searchParams, setSearchParams] = useSearchParams({
    idCompany,
    search: '',
    category: 'all',
    page: '1',
  })
  const isDeploySheetOpen = Boolean(useMatch('/templates/:name/deploy'))

  const page = Number(searchParams.get('page') ?? '1')
  const limit = Number(searchParams.get('limit')) || PAGE_SIZE
  const offset = (page - 1) * limit

  const deferredFilter = useDeferredValue(filterTerm)
  const [debouncedFilter, setDebouncedFilter] = useState<string>(deferredFilter)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedEventHandler = useCallback(
    debounce((value: string) => setDebouncedFilter(value), 500),
    []
  )

  useEffect(() => {
    debouncedEventHandler(deferredFilter)
  }, [deferredFilter, debouncedEventHandler])

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (debouncedFilter === '') {
          prev.delete('search')
        } else if (!prev.get('search')) {
          prev.set('search', debouncedFilter)
          prev.set('page', '1')
        } else {
          prev.set('search', debouncedFilter)
        }
        return prev
      },
      { replace: true }
    )
  }, [debouncedFilter, setSearchParams])

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (category === 'all') {
          prev.delete('category')
        } else if (!prev.get('category')) {
          prev.set('category', category)
          prev.set('page', '1')
        } else {
          prev.set('category', category)
        }
        return prev
      },
      { replace: true }
    )
  }, [category, setSearchParams])

  const { data } = useSuspenseQuery<TemplatesQuery, TemplatesQueryVariables>(
    TEMPLATES_QUERY,
    {
      variables: {
        idCompany,
        offset: PAGE_SIZE * (page - 1),
        limit: PAGE_SIZE,
        filters: {
          categorySlug: category !== 'all' ? category : undefined,
          name: debouncedFilter
            ? {
                contains: debouncedFilter,
                mode: 'insensitive',
              }
            : undefined,
        },
      },
    }
  )

  const templates = useMemo(() => data.templates.items, [data])
  const total = useMemo(() => data.templates.total, [data])

  const handlePagination = ({
    offset,
    limit,
  }: {
    offset: number
    limit: number
  }) => {
    const newPage = Math.ceil(offset / limit) + 1
    setSearchParams((prev) => {
      prev.set('page', String(newPage))
      prev.set('limit', String(limit))
      return prev
    })
  }

  const inProgressTemplateInstances = useMemo(() => {
    return templates
      .map((template) => template.instances)
      .flat()
      .filter((instance) => instance.status === 'inProgress')
  }, [templates])

  return (
    <>
      <div className="grid grid-cols-[repeat(auto-fill,_minmax(250px,_1fr))] gap-4">
        {templates.map((template) => {
          const isDeployInProgress = inProgressTemplateInstances.some(
            (instance) => instance.idTemplate === template.id
          )
          return (
            <TemplateCard
              key={template.id}
              id={template.id}
              name={template.name}
              displayName={template.displayName}
              description={template.description}
              logoUrl={template.logoUrl}
              categorySlug={template.categorySlug}
              isDeploying={isDeployInProgress}
            />
          )
        })}
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          onClick={() => {
            handlePagination({
              offset: offset - limit,
              limit,
            })
          }}
          disabled={offset === 0}
        >
          <span className="sr-only">Go to previous page</span>
          <ChevronLeft />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          onClick={() => {
            handlePagination({
              offset: offset + limit,
              limit,
            })
          }}
          disabled={offset + limit >= total}
        >
          <span className="sr-only">Go to next page</span>
          <ChevronRight />
        </Button>
      </div>
      <DeployTemplateSheet
        isOpen={isDeploySheetOpen}
        onClose={() =>
          navigateToPath({
            pathname: '/templates',
            options: { replace: true },
            preserveQuery: true,
          })
        }
      />
    </>
  )
}
